import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/base-components';
import { captureException } from '@rsos/utils/sentry';
import { ssoService } from '../../services/ssoService';
import type {
  Role,
  SSOConfigurationValues,
  IDPConfigurationValues,
  ConfigureSSOInitialValues,
  AppState,
  SSOSetupError,
} from '../../types/sso';
import ConfigureIDP from './ConfigureIDP';
import ConfigureSSO from './ConfigureSSO';
import ResetSSOConfiguration from './ResetSSOConfiguration';

class SSOSetupErrorImpl extends Error implements SSOSetupError {
  name = 'SSOSetupError';

  constructor(message: string) {
    super(message);
  }
}

const ManageSSO: React.FC = () => {
  const currentApp = useSelector(
    (state: AppState) => state.sinatra?.user?.currentRole?.application,
  );
  const orgId = useSelector(
    (state: AppState) => state.sinatra?.user?.currentOrg?.id,
  );
  const organizations = useSelector((state: AppState) => {
    return state.sinatra?.management?.organizations;
  });

  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    if (organizations && orgId && currentApp) {
      const orgRoles =
        organizations[orgId]?.apps?.[currentApp]?.roles?.records || [];
      const kvPairs: Role[] = orgRoles
        .map(role => ({
          value: role.name,
          label: role.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setRoles(kvPairs);
    }
  }, [organizations, orgId, currentApp]);

  const [isLoadingIDPSettings, setIsLoadingIDPSettings] =
    useState<boolean>(true);
  const [initialDomain, setInitialDomain] = useState<string>('');
  const [initialValues, setInitialValues] =
    useState<ConfigureSSOInitialValues | null>(null);

  const getSSOStatus = async (): Promise<void> => {
    if (!orgId || !currentApp) {
      setIsLoadingIDPSettings(false);
      return;
    }

    try {
      const data = await ssoService.getSSOStatus(orgId, currentApp);

      if (data.domain) {
        setInitialDomain(data.domain);
      }

      setInitialValues({
        selectedRole: data.selectedRole || '',
        ssoEnabled: data.ssoEnabled || false,
        ssoGroupAttribute: data.ssoGroupAttribute || '',
        jitEnabled: data.jitEnabled || false,
      });
    } catch (error) {
      captureException(
        new SSOSetupErrorImpl('Failed to initialize SSO settings'),
        {
          orgId,
          currentApp,
          error,
        },
      );
    } finally {
      setIsLoadingIDPSettings(false);
    }
  };

  useEffect(() => {
    getSSOStatus().catch(error => {
      captureException(
        new SSOSetupErrorImpl('Failed to initialize SSO settings'),
        {
          orgId,
          currentApp,
          error,
        },
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIDPSetup = async (
    values: IDPConfigurationValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ): Promise<void> => {
    if (!orgId) {
      setSubmitting(false);
      return;
    }

    try {
      setSubmitting(true);
      await ssoService.setupIdentityProvider(orgId, values);
    } catch (error) {
      // error handling handled in service
    } finally {
      setSubmitting(false);
    }
  };

  const handleSSOConfig = async (
    values: SSOConfigurationValues,
    done: (response?: {
      ssoEnabled?: boolean;
      selectedRole?: string;
      ssoGroupAttribute?: string;
      jitEnabled?: boolean;
    }) => void,
  ): Promise<void> => {
    if (!orgId || !currentApp) {
      done();
      return;
    }

    try {
      const result = await ssoService.configureSSOSettings(
        orgId,
        currentApp,
        values,
      );
      // Refetch with latest data to refill initialValues
      await getSSOStatus();
      done(result);
    } catch (error) {
      // error handling handled in service
      done();
    }
  };

  const handleSSOReset = async (): Promise<void> => {
    if (!orgId) {
      return;
    }

    try {
      await ssoService.resetSSOConfiguration(orgId);
      await getSSOStatus();
    } catch (error) {
      // done in service, should not be handled here
    }
  };

  const showResetComponent = Boolean(initialValues?.ssoEnabled);

  // No permission check here: this route is guarded by <RequireAccess
  // path="/admin/access/sso"> in app/Main.tsx, which resolves the same
  // MANAGE_SSO + manage_sso_0 rule from adminRouteAccess. Re-deciding it locally
  // is how the two used to disagree.
  return isLoadingIDPSettings ? (
    <div>Loading...</div>
  ) : (
    <>
      <Box mt={10}>
        <ConfigureIDP
          initialValues={{ domain: initialDomain }}
          onSubmit={handleIDPSetup}
        />
      </Box>
      <Box mt={20}>
        <ConfigureSSO
          initialValues={initialValues || undefined}
          roles={roles}
          onSubmit={handleSSOConfig}
        />
      </Box>
      {showResetComponent && (
        <Box mt={20}>
          <ResetSSOConfiguration onReset={handleSSOReset} />
        </Box>
      )}
    </>
  );
};

export default ManageSSO;
